import { BehaviorSubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CandleStickService } from '../api/candle-stick.service';
import { FiatExchangeRateService } from '../api/fiat-exchange-rate.service';
import { MetadataService } from '../api/metadata.service';
import { WebSocketService } from '../api/web-socket.service';
import { CurrenciesService } from '../api/currencies.service';
import { ExcahngeRate } from '../models/exchange-rate';
import { cloneDeep as _cloneDeep } from 'lodash-es';
import { PriceDataPoint, PriceDataSeries, PriceChartDuration, CryptoPriceData } from '../models/price-chart';
import { WebSocketTicker } from '../models/wss';
import { Injectable } from '@angular/core';
import { ChartColor } from '../models/chart';

@Injectable()
export class CryptoPriceService {
  private _globalCurrencyRate: number;
  private _globalCurrency: string;
  private _baseCurrency: string;
  private _mainCurrency: string;
  private _ticker: WebSocketTicker;
  private _duration: PriceChartDuration;
  private _priceHistory: PriceDataSeries;
  private _fallbackRates: ExcahngeRate[] = [];

  private _tickerSubscription: Subscription;
  private _historySubscription: Subscription;
  private _fallbackSubscription: Subscription;
  private _changesSubject = new BehaviorSubject<CryptoPriceData>(this.data);

  public constructor(
    private fiatExchangeRateService: FiatExchangeRateService,
    private wss: WebSocketService,
    private candlesService: CandleStickService,
    private metadataService: MetadataService,
    private currenciesService: CurrenciesService
  ) {
    this._globalCurrency = environment.currencies.globalCurrency;

    this.wss.connect(true);

    this.wss.connectionChanges.subscribe(() => {
      this.getTicker(this.pair);
    });
  }


  setCurrency(currency: string) {
    if (this._mainCurrency === currency) {
      return;
    }

    this._mainCurrency = currency;
    this.fetchFallbackRates(currency);
    this.getTicker(this.pair);
    this.getPriceHistory(this.pair, this.duration);
  }

  setDuration(duration: PriceChartDuration) {
    this._duration = duration;
    this.getPriceHistory(this.pair, this.duration);
  }

  setBaseCurrency(currency: string) {
    if(this._baseCurrency === currency) {
      return;
    }

    this._baseCurrency = currency;
    this.getGlobalCurrencyRate(this._baseCurrency);
    this.getPriceHistory(this.pair, this.duration);
    this.getTicker(this.pair);
  }

  setBaseCurrencyToUserCurrency() {
    this.metadataService.userCurrency().subscribe(response => {
      this.setBaseCurrency(response);
    }, err => {
      this.setBaseCurrency(environment.currencies.defaultCurrency);
    });
  }

  getGlobalCurrencyRate(baseCurrency: string) {
    if (!baseCurrency) {
      return;
    }

    this._globalCurrencyRate = null;

    this.fiatExchangeRateService.getGlobalCurrencyRate(baseCurrency).subscribe(response => {
      this._globalCurrencyRate = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._globalCurrencyRate = 0;
      this._changesSubject.next(this.data);
    })
  }

  getTicker(pair: string) {
    if (!this.pair) {
      return;
    }

    this._tickerSubscription?.unsubscribe();
    this._ticker = null;

    this._tickerSubscription = this.wss.ticker(pair).subscribe(response => {
      this._ticker = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._ticker = null;
      this._changesSubject.next(this.data);
    });
  }

  getPriceHistory(pair: string, duration: PriceChartDuration) {
    if (!this.pair || !this.duration) {
      return;
    }

    this._historySubscription?.unsubscribe();
    this._priceHistory = null;

    this._historySubscription = this.candlesService.getPriceHistory(pair, duration).subscribe(response => {
      this._priceHistory = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._priceHistory = null;
      this._changesSubject.next(this.data);
    })
  }

  private fetchFallbackRates(code: string) {
    this._fallbackRates = [];
    this._fallbackSubscription?.unsubscribe();

    if (!code) {
      return;
    }

    this._fallbackSubscription = this.currenciesService.getOne(code).subscribe(currency => {
      this._fallbackRates = [currency?.currentExchangeRate, currency?.currentUsdExchangeRate]
        .filter(rate => rate && rate.amount != null) as ExcahngeRate[];

      if (!this._ticker) {
        this._changesSubject.next(this.data);
      }
    }, () => {
      this._fallbackRates = [];
    });
  }

  private fallbackAmount(currencyCode: string): number {
    return this._fallbackRates.find(rate => rate?.currency === currencyCode)?.amount;
  }

  private get effectiveBaseLastPrice(): number {
    return this._ticker?.lastPrice ?? this.fallbackAmount(this._baseCurrency);
  }

  private get effectiveGlobalLastPrice(): number {
    if (this._ticker?.lastPrice != null) {
      return this._ticker.lastPrice * this._globalCurrencyRate;
    }
    return this.fallbackAmount(this._globalCurrency);
  }

  get data(): CryptoPriceData {
    const data: CryptoPriceData = {
      pair: this.pair,
      mainCurrency: this.mainCurrency,
      isPositvitePriceChange: this.isPositvitePriceChange,
      priceChangePercentage: this.priceChangePercentage,
      chartColor: this.chartColor,
      priceHistory: this.priceHistory,
      livePriceHistory: this.livePriceHistory,
      baseCurrency: {
        currency: this.baseCurrency,
        lastPrice: this.baseCurrencyLastPrice?.amount,
        priceHistory: this.baseCurrencyPriceHistory,
        priceChange: this.baseCurrencyPriceChange,
        dailyHigh: this._ticker?.dailyHigh,
        dailyLow: this._ticker?.dailyLow,
      },
      globalCurrency: {
        currency: this.globalCurrency,
        lastPrice: this.globalCurrencyLastPrice?.amount,
        priceHistory: this.globalCurrencyPriceHistory,
        priceChange: this.globalCurrencyPriceChange,
        dailyHigh: this._ticker?.dailyHigh ? this._ticker.dailyHigh * this._globalCurrencyRate : null,
        dailyLow: this._ticker?.dailyLow ? this._ticker.dailyLow * this._globalCurrencyRate : null,
      },
      defaultCurrency: null
    };

    data.defaultCurrency = this._globalCurrency ? data?.globalCurrency : data.baseCurrency;

    return data;
  }

  get valueChanges() {
    return this._changesSubject.asObservable();
  }

  get pair() {
    if (!this.mainCurrency || !this.baseCurrency) {
      return null;
    }

    return `${this.mainCurrency}-${this.baseCurrency}`;
  }

  get duration() {
    return this._duration;
  }

  get mainCurrency() {
    return this._mainCurrency;
  }

  get baseCurrency() {
    return this._baseCurrency;
  }

  get globalCurrency() {
    return this._globalCurrency;
  }

  get globalCurrencyRate() {
    return this._globalCurrencyRate;
  }

  get priceHistory() {
    return this._priceHistory;
  }

  get livePriceHistory() {
    const data = _cloneDeep(this._priceHistory);
    const time = (new Date()).getTime();
    const baseCurrencyLastPrice = this._ticker?.lastPrice;
    const globalCurrencyLastPrice = this._ticker?.lastPrice != null
      ? this._ticker.lastPrice * this._globalCurrencyRate
      : undefined;

    if (data?.baseCurrencyData?.data && baseCurrencyLastPrice) {
      data.baseCurrencyData.data = [{
        x: time,
        y: baseCurrencyLastPrice
      }, ...data.baseCurrencyData.data];

      if (data.baseCurrencyData.ohlcData) {
        data.baseCurrencyData.ohlcData = [{
          x: time,
          open: baseCurrencyLastPrice,
          high: baseCurrencyLastPrice,
          low: baseCurrencyLastPrice,
          close: baseCurrencyLastPrice,
        }, ...data.baseCurrencyData.ohlcData];
      }
    }

    if (data?.globalCurrencyData?.data && globalCurrencyLastPrice) {
      data.globalCurrencyData.data = [{
        x: time,
        y: globalCurrencyLastPrice
      }, ...data.globalCurrencyData.data];

      if (data.globalCurrencyData.ohlcData) {
        data.globalCurrencyData.ohlcData = [{
          x: time,
          open: globalCurrencyLastPrice,
          high: globalCurrencyLastPrice,
          low: globalCurrencyLastPrice,
          close: globalCurrencyLastPrice,
        }, ...data.globalCurrencyData.ohlcData];
      }
    }

    if (data?.globalCurrencyData || data?.baseCurrencyData) {
      data.defaultCurrencyData = this._globalCurrency ? data.globalCurrencyData : data.baseCurrencyData;
    }

    return data;
  }

  get baseCurrencyPriceHistory() {
    return this._priceHistory?.baseCurrencyData?.data;
  }

  get globalCurrencyPriceHistory() {
    return this._priceHistory?.globalCurrencyData?.data;
  }

  get globalCurrencyLastPrice(): ExcahngeRate {
    return {
      currency: this._globalCurrency,
      amount: this.effectiveGlobalLastPrice
    }
  }

  get baseCurrencyLastPrice(): ExcahngeRate {
    return {
      currency: this._baseCurrency,
      amount: this.effectiveBaseLastPrice
    }
  }

  get baseCurrencyPriceChange(): number {
    const oldestDataPoint = this.oldestBaseCurrencyDataPoint();
    return this.effectiveBaseLastPrice - oldestDataPoint?.y;
  }

  get globalCurrencyPriceChange(): number {
    const oldestDataPoint = this.oldestGlobalCurrencyDataPoint();
    return this.effectiveGlobalLastPrice - oldestDataPoint?.y;
  }

  get priceChangePercentage() {
    const oldestDataPoint = this.oldestBaseCurrencyDataPoint();
    return (this.baseCurrencyPriceChange / oldestDataPoint?.y) * 100;
  }

  get isPositvitePriceChange() {
    return this.baseCurrencyPriceChange >= 0;
  }

  get chartColor(): ChartColor {
    return this.baseCurrencyPriceChange < 0 ? ChartColor.Negative : ChartColor.Positive
  }

  private oldestBaseCurrencyDataPoint(): PriceDataPoint {
    const lastId = this?._priceHistory?.baseCurrencyData?.data?.length - 1;
    return this._priceHistory?.baseCurrencyData?.data?.[lastId];
  }

  private oldestGlobalCurrencyDataPoint(): PriceDataPoint {
    const lastId = this?._priceHistory?.globalCurrencyData?.data?.length - 1;
    return this._priceHistory?.globalCurrencyData?.data?.[lastId];
  }

  ngOnDestroy() {
    this._tickerSubscription?.unsubscribe();
    this._historySubscription?.unsubscribe();
    this._fallbackSubscription?.unsubscribe();
    this._changesSubject.complete();
  }
}
